import React, { useEffect, useState, useMemo, useRef } from "react";
import FA from "../../fa";
import {
  INK,
  MUTED,
  PROSE,
  GRID,
  CONTEXT,
  TRAY,
  ROW_DIVIDER,
  nestBg,
  MASTERY_OUTRIGHT,
  MASTERY_EVENTUALLY,
  MASTERY_NOT,
  MASTERY_NOT_INK,
  MASTERY_BANDS,
  SUBJECT_COLORS,
  LEVEL_REL,
  LEVEL_REL_ORDER,
} from "../tokens";
import { SCHOOL_MIN_LEARNERS } from "../model";
import {
  gradeLabel as baseGradeLabel, relSummary, relBreakdown, relChipText, relTone,
} from "../format";
import { UnitSchoolMatrix, spreadOf } from "./UnitSchoolMatrix";
import {
  TableCard, TableNote, SegControl, Select, GhostButton, Caret,
  Legend, Card, RailStat, StatRail, PlotNote, ControlBar, DisplayMenu,
  SectionHead,
} from "../ui";

// "Standards & Units" tab: per-unit mastery composition for one grade cohort of a
// single standardset, split mastered-outright vs. eventually (ABii-assisted) vs.
// not-yet. Self-contained -- owns its grade/standardset/subject/sort/mode/coverage
// controls and its own fetch (the parent only hands it api + org + windowSpec),
// mirroring how EngagementTab keeps its bundle local. Composition bars are plain
// divs (not chart.js): many rows + a per-standard drill-down read better as a
// stacked list than as a chart canvas.
//
// A row expands into either its standards or its *schools* ("Expand into"), and
// each of those expands once more into the other -- so unit -> schools -> that
// school's standards and unit -> standards -> that standard's schools are the same
// two levels read in either order. Aligned bars on a shared axis is why the
// comparison lives in the expansion rather than in a popup: schools are compared
// against each other and against the pinned district row above them.

// Validated status palette, defined once in tokens.js (the Overview's gap spark
// draws the same composition at a smaller size). Aliased to the short local names
// this file already reads by.
const C_OUT = MASTERY_OUTRIGHT; // mastered outright -- on their own
const C_EVT = MASTERY_EVENTUALLY; // eventually mastered -- ABii-assisted re-teach
const C_NOT = MASTERY_NOT; // not yet mastered -- the gap / focus signal
const C_NOT_INK = MASTERY_NOT_INK; // accessible amber for text on white

// Grades offered come from the payload's available_grades, not a fixed list.
const GRADE_ALL = "all";
// thin wrapper over the shared scale label, adding this tab's "all" sentinel
const gradeLabel = (g) => (g === GRADE_ALL ? "All" : baseGradeLabel(g));
const cohortLabel = (g) =>
  g === GRADE_ALL ? "All grades" : `Grade ${gradeLabel(g)}`;

// Per-school payloads are cached module-level, not in a ref: the dashboard swaps
// the whole route body for a spinner while it reloads, unmounting this tab, and a
// ref would be wiped on every page navigation (the same lesson as useSegmentDrill).
// groupCache holds every unit's school split (one fetch covers all of them);
// stdCache holds one unit's per-standard split, which is per-unit by construction.
const groupCache = new Map();
const stdCache = new Map();

// State the tab owns that belongs in a shared link: which cohort, whether the
// comparison is open, and which unit is open. Same replaceState
// mirroring the dashboard's other view state uses (state is the source of truth,
// the URL is the mirror) so a link lands the recipient on this exact view.
const mirror = (updates) => {
  const qp = new URLSearchParams(window.location.search);
  Object.entries(updates).forEach(([k, v]) => {
    if (v === null || v === undefined || v === "") qp.delete(k);
    else qp.set(k, String(v));
  });
  const qs = qp.toString();
  window.history.replaceState(
    null,
    "",
    qs ? `${window.location.pathname}?${qs}` : window.location.pathname
  );
};

const seedGrade = () => {
  const g = new URLSearchParams(window.location.search).get("grade");
  if (g === GRADE_ALL) return GRADE_ALL;
  const n = parseInt(g, 10);
  return Number.isNaN(n) ? 2 : n;
};
const seedEnum = (key, allowed, fallback) => {
  const v = new URLSearchParams(window.location.search).get(key);
  return allowed.includes(v) ? v : fallback;
};
const levelLabel = (l) =>
  l == null ? "" : l === 0 ? "K" : l <= -1 ? "PreK" : String(l);

// Mirrors UnitMasteryStats.CONTENT_LEVELS. "all" is the default and is the one
// value kept out of the URL.
const CONTENT_LEVELS = ["all", "on", "below", "above"];
const CONTENT_LEVEL_LABELS = {
  all: "All levels",
  on: "On grade",
  below: "Below",
  above: "Above",
};

const pct = (n, d) => (d ? Math.round((100 * n) / d) : 0);

const SORTERS = {
  focus: (a, b) => b.not_mastered / b.total - a.not_mastered / a.total,
  lift: (a, b) => b.eventually_mastered / b.total - a.eventually_mastered / a.total,
  activity: (a, b) => b.total - a.total,
  // Matrix only: how far apart the schools are on this unit. Units with no
  // computable spread (fewer than two schools clearing the learner bar) sort
  // last rather than to the top of a "most divergent" list.
  spread: (a, b) => (b._spread == null ? -1 : b._spread) -
    (a._spread == null ? -1 : a._spread),
};

const COVERAGE_OPTIONS = [1, 10, 15, 30];

const Seg = ({ segStyle, children, title, round }) => (
  <div
    title={title}
    style={{
      height: "100%",
      minWidth: children ? 0 : 2,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      overflow: "hidden",
      borderRadius: round,
      ...segStyle,
    }}
  >
    {children}
  </div>
);

// One composition bar (a unit row's bar, or a standard sub-row's bar).
//
// The full-size bar: share/count modes, the figure inside each segment where it
// fits, and rounding on the two outer ends so the three read as one track. The
// Overview's CompositionSpark draws the same three bands at card size with none
// of that.
const CompositionBar = ({ rec, mode, scale }) => {
  return (
    <div style={{ display: "flex", gap: 2, height: 24 }}>
      {MASTERY_BANDS.map((band, i) => {
        const count = rec[band.key];
        const frac = rec.total ? count / rec.total : 0;
        const flexBasis =
          mode === "share" ? `${frac * 100}%` : `${(count / scale) * 100}%`;
        const showLabel = (mode === "share" ? frac : count / scale) >= 0.09;
        const round =
          i === 0 ? "5px 2px 2px 5px" : i === 2 ? "2px 5px 5px 2px" : 2;
        return (
          <div key={band.key} style={{ flex: `0 0 ${flexBasis}`, minWidth: 2 }}>
            <Seg
              round={round}
              segStyle={{ background: band.color, height: 24 }}
              title={`${band.label}: ${count} (${pct(count, rec.total)}%)`}
            >
              {showLabel && (
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#fff",
                    fontVariantNumeric: "tabular-nums",
                    textShadow: "0 1px 1px rgba(0,0,0,.28)",
                    whiteSpace: "nowrap",
                  }}
                >
                  {mode === "share" ? `${pct(count, rec.total)}%` : count}
                </span>
              )}
            </Seg>
          </div>
        );
      })}
    </div>
  );
};

// The on-grade tile's composition: below | on | above, left to right.
//
// A number line, which is the whole point -- a single filled meter read
// backwards, because the fill was the "on grade" share while the empty remainder
// was the "below" share sitting on the wrong side of it.
//
// This is a DIVERGING encoding and it is built to that spec: two poles plus a
// neutral midpoint. The midpoint is the pale track (on grade, the unremarkable
// case) and both poles are shaded, so the off-grade work is what draws the eye
// -- the same rule the row chips use, rather than a second visual language for
// one variable. The poles get no hue for the reason recorded in tokens.js: an
// exhaustive OKLCH search against MASTERY_OUTRIGHT/EVENTUALLY/NOT tops out at
// tritan ΔE 7.6 even with the subject hues excluded from the comparison.
// Direction is carried by position, as it is in every diverging bar; the two
// poles are additionally separated by texture (`above` is hatched) so they do
// not read as one interrupted band in a district with real acceleration.
const HATCH = `repeating-linear-gradient(45deg, ${PROSE} 0 2px, ${CONTEXT} 2px 4px)`;
const LEVEL_BANDS = [
  { key: "below", background: PROSE },
  { key: "on", background: GRID },
  { key: "above", background: HATCH },
  { key: "unknown", background: CONTEXT },
];

// Each band selects its own content level, so the summary is also the way into
// the filtered view -- the reading and the control are the same object.
//
// The row is taller than the 8px band it draws: the band stays thin (the mark
// spec) while the button around it is a 16px target, since a 1%-wide band would
// otherwise be a 4px one. It is NOT widened to a comfortable size -- that would
// misstate the share, and this bar's whole job is the share. The ControlBar's
// "Content level" is the reliable, keyboard-ordered path to the same four
// values; these bands are the shortcut, not the only route.
const LevelCompositionBar = ({ byLevel, total, onSelect }) => (
  <div
    style={{
      display: "flex",
      gap: 2,
      height: 16,
      marginTop: 4,
      alignItems: "center",
    }}
  >
    {LEVEL_BANDS.map(({ key, background }) => {
      const count = (byLevel && byLevel[key]) || 0;
      if (!count) return null;
      // `unknown` is not selectable: content_level admits the three directions
      // only, and "the events we could not place" is not a curriculum position
      // anyone can ask the backend for.
      const selectable = !!onSelect && key !== "unknown";
      const reading =
        `${LEVEL_REL[key].long}: ${count.toLocaleString()} (${pct(count, total)}%)`;
      const band = (
        <div style={{ height: 8, width: "100%", background, borderRadius: 4 }} />
      );
      const shared = {
        flex: `0 0 ${(count / total) * 100}%`,
        minWidth: selectable ? 4 : 2,
        height: "100%",
        display: "flex",
        alignItems: "center",
      };
      return selectable ? (
        <button
          key={key}
          type="button"
          onClick={() => onSelect(key)}
          title={`${reading} — show only this`}
          aria-label={`Show only ${LEVEL_REL[key].long} — ${reading}`}
          style={{
            ...shared,
            padding: 0,
            border: 0,
            background: "transparent",
            cursor: "pointer",
          }}
        >
          {band}
        </button>
      ) : (
        <div key={key} style={shared} title={reading}>
          {band}
        </div>
      );
    })}
  </div>
);

// Tints and inks derived from SUBJECT_COLORS rather than restated as literals:
// the row is about to carry a second chip, and two chip palettes drifting apart
// is how this rots. The ink is a darkened step of the same hue -- the raw series
// hue is a mark color, not a text color, at 10px.
const SUBJECT_CHIP = {
  MATH: { bg: "rgba(42, 120, 214, .14)", fg: "#1c5cab", text: "MATH" },
  READING: { bg: "rgba(74, 58, 167, .13)", fg: SUBJECT_COLORS.READING, text: "READ" },
};

const CHIP_BASE = {
  fontSize: 10,
  letterSpacing: ".04em",
  textTransform: "uppercase",
  fontWeight: 700,
  padding: "1px 6px",
  borderRadius: 5,
  whiteSpace: "nowrap",
};

const Chip = ({ subject }) => {
  const c = SUBJECT_CHIP[subject] || {
    bg: "#eee",
    fg: MUTED,
    text: subject,
  };
  return (
    <span
      style={{
        ...CHIP_BASE,
        background: c.bg,
        color: c.fg,
      }}
    >
      {c.text}
    </span>
  );
};

// Where this unit sits relative to the grade of the learner who worked it.
//
// Carries the unit's own level (a property of the unit, always true) plus the
// grade-relative reading: a direction as a GLYPH AND A WORD where the unit was
// worked from one grade, otherwise the share of it that was on grade. Never a
// hue -- see LEVEL_REL in tokens.js for why this encoding has no color to spend.
// The surface weight is the off-grade share (LEVEL_OFF_STEPS), so an off-grade
// row is what catches a scanning eye and an on-grade one stays quiet.
const LevelChip = ({ level, byLevel }) => {
  const rel = relSummary(byLevel);
  const split = relBreakdown(byLevel);
  const lvl = level == null ? "—" : levelLabel(level);
  // Surface weight comes from the off-grade *share* (so it also fires on the
  // mixed rows that dominate an all-grades roster); the glyph, when there is an
  // unambiguous one, comes from the direction.
  const tone = relTone(byLevel);
  const glyph = rel ? rel.glyph : "";
  const text = relChipText(byLevel);
  return (
    <span
      style={{
        ...CHIP_BASE,
        fontWeight: 600,
        letterSpacing: ".02em",
        textTransform: "none",
        fontSize: 10.5,
        background: tone.tint,
        color: tone.ink,
        display: "inline-flex",
        alignItems: "center",
        gap: 3,
      }}
      title={
        split
          ? `Content level vs. the working learner's grade — ${split}`
          : "Content level"
      }
    >
      <span style={{ color: MUTED }}>Lv {lvl}</span>
      {glyph && <span aria-hidden="true">{glyph}</span>}
      {text && <span>{text}</span>}
    </span>
  );
};

// Column geometry, shared by the header band, the unit rows and the standard
// sub-rows so all three stay in vertical register. This "table" is CSS grid
// rather than a <table> because each row's middle cell is a flex composition
// bar; the header band is styled to match ui.js's HeadRow so it still reads as
// the same kind of object as the worklist tables.
// Caret leads the row, the way the Schools worklist does it (FocusTab), so the
// expansion affordance sits in the same place everywhere in the dashboard.
const GRID_COLS = "26px 270px 1fr 70px";
const SUB_GRID_COLS = "26px 250px 1fr 70px";
const COL_GAP = 14;

// One labelled composition bar inside an expansion: a standard row, a school row,
// or the pinned district row they are compared against. Every variant shares this
// geometry so bars stay on a common axis -- the whole point of comparing here.
const SubRow = ({
  label,
  note,
  title,
  rec,
  mode,
  scale,
  strong,
  dim,
  marker,
  expanded,
  onToggle,
  canExpand,
  cols = SUB_GRID_COLS,
}) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: cols,
      alignItems: "center",
      gap: COL_GAP,
      padding: "4px 0",
      opacity: dim ? 0.62 : 1,
    }}
  >
    {onToggle ? (
      <Caret
        open={expanded}
        onClick={onToggle}
        disabled={!canExpand}
        title={canExpand ? "Compare" : "No detail"}
      />
    ) : (
      <div />
    )}
    <div style={{ minWidth: 0, display: "flex", alignItems: "center", gap: 5 }}>
      {marker}
      <span
        style={{
          fontSize: 11.5,
          fontWeight: strong ? 650 : 400,
          color: strong ? INK : MUTED,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          cursor: title ? "help" : "default",
        }}
        title={title}
      >
        {label}
      </span>
      {note}
    </div>
    <div style={{ transform: "scaleY(.82)" }}>
      <CompositionBar rec={rec} mode={mode} scale={scale} />
    </div>
    <div
      style={{
        textAlign: "right",
        fontSize: 11.5,
        color: MUTED,
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {rec.total} · {rec.learners}l
    </div>
  </div>
);

const ThinNote = () => (
  <span style={{ fontSize: 10, color: C_NOT_INK, fontWeight: 600, whiteSpace: "nowrap" }}>
    thin
  </span>
);

const SubNote = ({ children }) => (
  <div style={{ fontSize: 11.5, color: MUTED, padding: "6px 0 2px" }}>{children}</div>
);

// The pinned reference row: what the rows under it decompose. Part-vs-whole is
// the intended read (the same reference idiom the Schools worklist uses for a
// classroom inside its cohort), so it is labelled, not just visually distinct.
const RefRow = ({ label, rec, mode, scale }) => (
  <>
    <SubRow label={label} rec={rec} mode={mode} scale={scale} strong />
    <div style={{ borderTop: `1px solid ${ROW_DIVIDER}`, margin: "2px 0 4px" }} />
  </>
);

// A school's row, in either order: at level 2 under a unit, or at level 3 under a
// standard. `selected` marks the school chosen in the page's School control --
// the control is left enabled during a comparison (all schools must be present
// for it to mean anything), so it marks rather than filters.
const SchoolRow = ({ row, selected, ...rest }) => (
  <SubRow
    label={row.group}
    marker={
      selected ? (
        <span title="Selected in the School control" style={{ color: C_EVT, fontSize: 10 }}>
          ▸
        </span>
      ) : null
    }
    note={row.learners < SCHOOL_MIN_LEARNERS ? <ThinNote /> : null}
    dim={row.learners < SCHOOL_MIN_LEARNERS}
    rec={row}
    {...rest}
  />
);

const UnitRow = ({
  rec,
  mode,
  scale,
  standards,
  expanded,
  onToggle,
  thin,
  split,
  detail,
  sub,
  onSub,
  selectedSchool,
}) => {
  const stds = standards || [];
  const compare = split === "schools";
  const hasStandards = stds.length > 0;
  // In compare mode a unit always expands (its schools come from the detail
  // fetch); otherwise it expands only if the payload carried standards.
  const canExpand = compare || hasStandards;
  const schoolRowsFor = (standardId) =>
    ((detail && detail.standardsByGroup) || {})[String(standardId)] || [];

  // Level 3, either order. Both are served by the same per-unit detail fetch.
  const level3 = (rows, refRec, refLabel, emptyText) => {
    if (detail && detail.loading)
      return <SubNote>
        <FA color="gray" icon="spinner" spin /> Loading school detail…
      </SubNote>;
    if (detail && detail.error)
      return <SubNote>Couldn’t load the school comparison.</SubNote>;
    if (!rows.length) return <SubNote>{emptyText}</SubNote>;
    return (
      <div style={{ padding: "2px 0 6px 18px", background: nestBg(2) }}>
        <RefRow label={refLabel} rec={refRec} mode={mode} scale={scale} />
        {rows}
      </div>
    );
  };

  return (
    <div style={{ borderTop: `1px solid ${ROW_DIVIDER}`, opacity: thin ? 0.6 : 1 }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: GRID_COLS,
          alignItems: "center",
          gap: COL_GAP,
          padding: "7px 4px 7px 4px",
        }}
      >
        <Caret
          open={expanded}
          onClick={onToggle}
          disabled={!canExpand}
          title={
            canExpand
              ? compare
                ? "Compare schools"
                : "Show standards"
              : "No standard detail"
          }
        />
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 550,
              color: INK,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            title={rec.unit}
          >
            {rec.unit}
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 7,
              marginTop: 2,
            }}
          >
            <Chip subject={rec.subject} />
            <LevelChip level={rec.level} byLevel={rec.by_level} />
            <span
              style={{
                fontSize: 11.5,
                color: MUTED,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {rec.learners} learners
              {thin && (
                <span style={{ color: C_NOT_INK, fontWeight: 600 }}>
                  {" "}
                  · thin
                </span>
              )}
            </span>
          </div>
        </div>
        <CompositionBar rec={rec} mode={mode} scale={scale} />
        <div
          style={{
            textAlign: "right",
            fontSize: 12.5,
            color: INK,
            fontWeight: 600,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {rec.total.toLocaleString()}
        </div>
      </div>
      {expanded && canExpand && (
        <div style={{ padding: "2px 8px 10px 20px", background: nestBg(1) }}>
          {compare
            ? /* unit -> schools -> that school's standards */
              (detail && detail.loading && !(detail.byGroup || []).length) ? (
                <SubNote>
                  <FA color="gray" icon="spinner" spin /> Loading schools…
                </SubNote>
              ) : detail && detail.error && !(detail.byGroup || []).length ? (
                <SubNote>Couldn’t load the school comparison.</SubNote>
              ) : !((detail && detail.byGroup) || []).length ? (
                <SubNote>No school has activity on this unit yet.</SubNote>
              ) : (
                <>
                  <RefRow
                    label="District (all schools)"
                    rec={rec}
                    mode={mode}
                    scale={scale}
                  />
                  {detail.byGroup.map((g) => (
                    <React.Fragment key={g.group_id}>
                      <SchoolRow
                        row={g}
                        selected={
                          selectedSchool != null &&
                          String(selectedSchool) === String(g.group_id)
                        }
                        mode={mode}
                        scale={scale}
                        expanded={String(sub) === `school:${g.group_id}`}
                        onToggle={() => onSub(`school:${g.group_id}`)}
                        canExpand={hasStandards}
                      />
                      {String(sub) === `school:${g.group_id}` &&
                        level3(
                          stds
                            .map((s) => ({
                              s,
                              row: schoolRowsFor(s.standard_id).find(
                                (r) => String(r.group_id) === String(g.group_id)
                              ),
                            }))
                            .filter((x) => x.row)
                            .map(({ s, row }) => (
                              <SubRow
                                key={s.standard_id}
                                label={s.standard}
                                title={
                                  s.description
                                    ? `${s.standard} — ${s.description}`
                                    : s.standard
                                }
                                rec={row}
                                mode={mode}
                                scale={scale}
                              />
                            )),
                          g,
                          `${g.group} — this unit`,
                          `No per-standard detail for ${g.group}.`
                        )}
                    </React.Fragment>
                  ))}
                </>
              )
            : /* unit -> standards -> that standard's schools */
              stds.map((s) => (
                <React.Fragment key={s.standard_id}>
                  <SubRow
                    label={s.standard}
                    title={
                      s.description
                        ? `${s.standard} — ${s.description}`
                        : s.standard
                    }
                    rec={s}
                    mode={mode}
                    scale={scale}
                    expanded={String(sub) === `std:${s.standard_id}`}
                    onToggle={
                      onSub ? () => onSub(`std:${s.standard_id}`) : undefined
                    }
                    canExpand={!!onSub}
                  />
                  {String(sub) === `std:${s.standard_id}` &&
                    level3(
                      schoolRowsFor(s.standard_id).map((g) => (
                        <SchoolRow
                          key={g.group_id}
                          row={g}
                          selected={
                            selectedSchool != null &&
                            String(selectedSchool) === String(g.group_id)
                          }
                          mode={mode}
                          scale={scale}
                        />
                      )),
                      s,
                      `District — ${s.standard}`,
                      "No school has activity on this standard yet."
                    )}
                </React.Fragment>
              ))}
        </div>
      )}
    </div>
  );
};

const UnitMasteryTab = ({
  api,
  org,
  windowSpec,
  orgunits = [],
  canScopeSchool = false,
  canPickStandardSet = false,
  // The page's lens SubTabs, hosted on this tab's ControlBar. The scope controls
  // belong to this component (it owns the cohort state and the fetch), so the bar
  // has to be rendered here — the page hands down the navigation that shares it.
  tabs,
}) => {
  const [grade, setGrade] = useState(seedGrade);
  const [ssOverride, setSsOverride] = useState(null); // null => org preferred
  const [subject, setSubject] = useState(""); // "" => both
  const [sort, setSort] = useState("focus");
  const [mode, setMode] = useState("share");
  const [minLearners, setMinLearners] = useState(15);
  // Which content levels to count, relative to the grade of the learner who
  // did the work -- so this is answerable on an all-grades roster too, which is
  // exactly what the old unit-vs-selected-grade version could not do. "all" is
  // the default because off-level work is real remediation/acceleration signal,
  // not an error to be filtered away. Server-side (it narrows the fold, so the
  // per-school splits and the matrix narrow with it), hence part of `params`.
  const [contentLevel, setContentLevel] = useState(
    () => seedEnum("clevel", CONTENT_LEVELS, "all")
  );
  const [showThin, setShowThin] = useState(false);
  const [expanded, setExpanded] = useState(() => {
    const u = parseInt(
      new URLSearchParams(window.location.search).get("unit"), 10);
    return Number.isNaN(u) ? null : u;
  });
  const [school, setSchool] = useState(null); // null => all schools (org scope)
  // What a row expands into. "schools" turns the expansion into the per-school
  // comparison; the two orders (unit->schools->standards and
  // unit->standards->schools) are served by the same per-unit fetch.
  const [split, setSplit] = useState(() =>
    new URLSearchParams(window.location.search).get("split") === "schools"
      ? "schools"
      : "standards"
  );
  // "matrix" is the scan view (units x schools, one measure per cell); the list
  // stays the default and is also the table view the matrix defers to for figures.
  const [view, setView] = useState(() =>
    new URLSearchParams(window.location.search).get("view") === "matrix"
      ? "matrix"
      : "list"
  );
  const [cellMetric, setCellMetric] = useState(() =>
    new URLSearchParams(window.location.search).get("cell") === "delta"
      ? "delta"
      : "reached"
  );
  const [sub, setSub] = useState(null);  // open level-3 row: std:<id>|school:<id>
  const [detailState, setDetailState] = useState({});
  const [data, setData] = useState({ loading: true });
  const firstLoad = useRef(true);
  // Grades to offer -- populated from the response so only grades with real
  // in-window activity are selectable (ABii content is K-5; no 6-8 unless data).
  // Persisted across refetches so the grade control doesn't flicker while loading.
  const [availGrades, setAvailGrades] = useState(null);

  const orgId = org && org.id;

  // A superadmin switching orgs (or an org whose OrgUnit list changed) must drop
  // a now-invalid school selection back to "all schools".
  useEffect(() => {
    setSchool((cur) =>
      cur != null && !orgunits.some((o) => String(o.id) === String(cur))
        ? null
        : cur
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, orgunits]);

  // A comparison needs every school in view, so the School control can't also
  // narrow the list -- in compare mode it is overridden (and the chosen school is
  // marked in the rows instead). Outside compare mode a school-scoped list can't
  // nest a district-wide comparison under it, so the level-3 carets go away.
  const canCompareSchools = canScopeSchool && orgunits.length > 1;
  const matrix = view === "matrix" && canCompareSchools;
  const compare = split === "schools" && canCompareSchools && !matrix;
  // Both comparison views need the district's own rows: the matrix rows and the
  // list's expansion reference are what the school columns/rows decompose.
  const effSchool = compare || matrix ? null : school;
  const subEnabled = canCompareSchools && (compare || !school);

  const params = useMemo(
    () => ({
      scope: effSchool ? "orgunit" : "organization",
      id: effSchool || orgId,
      grade,
      start: windowSpec && windowSpec.start,
      end: windowSpec && windowSpec.end,
      standardset:
        ssOverride || (windowSpec && windowSpec.ssParam) || undefined,
      subject: subject || undefined,
      content_level: contentLevel === "all" ? undefined : contentLevel,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [effSchool, orgId, grade, ssOverride, subject, contentLevel, windowSpec]
  );
  const pKey = JSON.stringify(params);

  useEffect(() => {
    if (!orgId) {
      setData({ awaitingOrg: true });
      return;
    }
    let cancelled = false;
    setData({ loading: true });
    // Collapse the open row when the cohort/filters change under it -- but not on
    // the first load, which would throw away a ?unit= deep link before the
    // response that justifies it has even arrived.
    if (!firstLoad.current) {
      setExpanded(null);
      setSub(null);
    }
    firstLoad.current = false;
    api
      .fetchWindowedUnitMastery(params)
      .then((res) => {
        if (cancelled) return;
        setData({ loading: false, ...res });
        const ag = (res && res.available_grades) || [];
        if (ag.length) {
          setAvailGrades(ag);
          // If the current grade has no data, jump to the closest one that does.
          if (grade !== GRADE_ALL && !ag.includes(grade)) {
            setGrade(
              ag.reduce((a, b) =>
                Math.abs(b - grade) < Math.abs(a - grade) ? b : a
              )
            );
          }
        }
      })
      .catch(() => {
        if (!cancelled) setData({ loading: false, error: true });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, pKey]);

  // Per-school detail for the open unit. One request carries both levels (that
  // unit's school split and its per-standard-per-school split), so opening a
  // school row after its unit costs nothing, and the unit-level splits of every
  // *other* unit arrive with the first fetch and render instantly thereafter.
  const stdKey = `${pKey}|${expanded}`;
  useEffect(() => {
    if (!orgId) return;
    // The matrix needs every unit's school split up front; the list needs it only
    // for the unit being expanded. Either way one request carries both levels.
    const needGroups = (compare || matrix) && !groupCache.has(pKey);
    const needStds = sub != null && !stdCache.has(stdKey);
    const wantUnit = expanded != null && subEnabled;
    if (!needGroups && !(needStds && wantUnit)) {
      setDetailState((s) => (s.loading || s.error ? {} : s));
      return;
    }
    let cancelled = false;
    setDetailState({ loading: true });
    api
      .fetchWindowedUnitMastery({
        ...params,
        // the split is district-wide by construction; see effSchool
        scope: "organization",
        id: orgId,
        by: "school",
        ...(wantUnit ? { unit: expanded } : {}),
      })
      .then((res) => {
        if (cancelled) return;
        groupCache.set(pKey, {
          groups: (res && res.groups) || [],
          byGroup: (res && res.by_group) || {},
        });
        if (wantUnit)
          stdCache.set(stdKey, (res && res.standards_by_group) || {});
        setDetailState({});
      })
      .catch(() => {
        if (!cancelled) setDetailState({ error: true });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pKey, stdKey, expanded, sub, compare, matrix, subEnabled, orgId]);

  const cached = groupCache.get(pKey) || { groups: [], byGroup: {} };
  const detail = useMemo(
    () => ({
      loading: !!detailState.loading,
      error: !!detailState.error,
      byGroup: cached.byGroup[String(expanded)] || [],
      standardsByGroup: stdCache.get(stdKey) || {},
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [detailState, pKey, stdKey, expanded]
  );

  const handleSetGrade = (g) => {
    setGrade(g);
    setExpanded(null);
    mirror({ grade: g === 2 ? null : g, unit: null });
  };
  const handleSetContentLevel = (v) => {
    setContentLevel(v);
    setExpanded(null);
    setSub(null);
    // Mirrored: this changes *what is being described*, so a shared link that
    // dropped it would show a different reading than the sender saw. The
    // display-only settings (sort/bar/min learners) deliberately stay out.
    mirror({ clevel: v === "all" ? null : v, unit: null });
  };
  const handleSetSplit = (s) => {
    setSplit(s);
    setSub(null);
    mirror({ split: s === "standards" ? null : s });
  };
  const handleSetView = (v) => {
    setView(v);
    setExpanded(null);
    setSub(null);
    // Spread is the matrix's reason to exist -- Focus ranks rows by a measure
    // that doesn't use the columns at all, so entering the matrix on it shows a
    // wall of cells in an order the view can't explain. It also only exists in
    // the matrix, so leaving on it would freeze the list in a stale order.
    if (v === "matrix" && sort !== "activity") setSort("spread");
    if (v !== "matrix" && sort === "spread") setSort("focus");
    mirror({ view: v === "list" ? null : v, unit: null });
  };
  const handleSetCellMetric = (m) => {
    setCellMetric(m);
    mirror({ cell: m === "reached" ? null : m });
  };
  const handleToggleUnit = (unitId) => {
    const next = expanded === unitId ? null : unitId;
    setExpanded(next);
    setSub(null);
    mirror({ unit: next });
  };
  const handleSub = (key) => setSub((cur) => (cur === key ? null : key));

  const { ranked, thin, totals, levelTotals } = useMemo(() => {
    // Content level is filtered server-side now (it narrows which mastery events
    // are folded, not which rows survive), so there is nothing to drop here.
    const units = (data && data.units) || [];
    const ranked = units
      .filter((u) => u.learners >= minLearners)
      // _spread is what the matrix's "Spread" sort ranks on; attached here so
      // there is one sort path over the rows both views share.
      .map((u) =>
        matrix
          ? { ...u, _spread: spreadOf(cached.byGroup[String(u.unit_id)]).spread }
          : u
      )
      .sort(SORTERS[sort]);
    const thin = units.filter((u) => u.learners < minLearners);
    const totals = ranked.reduce(
      (a, u) => ({
        out: a.out + u.mastered_outright,
        evt: a.evt + u.eventually_mastered,
        not: a.not + u.not_mastered,
      }),
      { out: 0, evt: 0, not: 0 }
    );
    // Summed over the rows actually shown, the same way `totals` is, so the
    // headline agrees with the list under it rather than with a wider cohort.
    const levelTotals = ranked.reduce(
      (a, u) => {
        const b = u.by_level || {};
        [...LEVEL_REL_ORDER, "unknown"].forEach((k) => {
          a[k] += b[k] || 0;
        });
        return a;
      },
      { below: 0, on: 0, above: 0, unknown: 0 }
    );
    return { ranked, thin, totals, levelTotals };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, minLearners, sort, matrix, detailState, pKey]);

  const scale = Math.max(1, ...ranked.map((u) => u.total), ...thin.map((u) => u.total));
  const totEvents = totals.out + totals.evt + totals.not;
  const levelShown = levelTotals.below + levelTotals.on + levelTotals.above +
    levelTotals.unknown;
  const ssOptions = (data.available_standardsets || []).map((s) => [
    s.shortname,
    s.shortname,
  ]);
  const curSs = ssOverride || data.standardset;
  const gradeChoices = [
    ...(availGrades && availGrades.length
      ? availGrades
      : grade === GRADE_ALL
      ? []
      : [grade]),
    GRADE_ALL,
  ];
  const selectedSchoolName = (
    orgunits.find((o) => String(o.id) === String(school)) || {}
  ).name;

  // How many display settings are off default, for the collapsed menu's badge.
  // The default sort is view-dependent (handleSetView swaps focus/spread with the
  // view, since Spread only exists in the matrix), so it's compared per view
  // rather than against a constant — otherwise the matrix would open showing "1
  // changed" for a setting the reader never touched.
  const displayChanged =
    (sort !== (matrix ? "spread" : "focus") ? 1 : 0) +
    (!matrix && mode !== "share" ? 1 : 0) +
    (minLearners !== 15 ? 1 : 0);

  // Body is loading/awaiting/error/empty or the chart, but the controls stay
  // mounted so the grade/standardset pickers don't vanish while a fetch is in
  // flight.
  let body;
  if (data.awaitingOrg)
    body = <div style={{ color: MUTED, padding: 40 }}>Select an organization above to see unit mastery.</div>;
  else if (data.loading)
    body = (
      <div style={{ color: MUTED, padding: 40 }}>
        <FA color="gray" icon="spinner" spin /> Loading unit mastery…
      </div>
    );
  else if (data.error)
    body = <div style={{ color: MUTED, padding: 40 }}>Couldn’t load unit mastery for this cohort.</div>;

  return (
    <div>
      {/* SCOPE — the four choices that define which cohort is being described,
          and between them the whole fetch key. One inline row with the page's
          lens tabs, replacing a stacked block of eleven equal-weight controls in
          which "Grade" looked no more consequential than "Bar". */}
      <ControlBar tabs={tabs}>
        {canScopeSchool && orgunits.length > 0 && (
          <Select
            inline
            label={compare ? "School (marked only)" : "School"}
            title={
              compare
                ? "A comparison needs every school in view, so this only marks a school in the expanded rows."
                : undefined
            }
            value={school}
            onChange={(v) => setSchool(v || null)}
            maxWidth={200}
            options={[
              ["", "All schools"],
              ...orgunits.map((s) => [s.id, s.name]),
            ]}
          />
        )}
        <SegControl
          inline
          label="Grade"
          value={grade}
          onChange={handleSetGrade}
          options={gradeChoices.map((g) => [g, gradeLabel(g)])}
        />
        <SegControl
          inline
          label="Subject"
          value={subject}
          onChange={setSubject}
          options={[
            ["", "All"],
            ["MATH", "Math"],
            ["READING", "Reading"],
          ]}
        />
        {/* A content facet, not a display setting: it changes which mastery
            events are counted, so it belongs in the fetch key and up here with
            the other choices that define the reading. Ungated by grade — the
            comparison is per learner, so it holds for "All grades" too. */}
        <SegControl
          inline
          label="Content level"
          title="Content's grade compared with the grade of the learner who worked it"
          value={contentLevel}
          onChange={handleSetContentLevel}
          options={CONTENT_LEVELS.map((v) => [v, CONTENT_LEVEL_LABELS[v]])}
        />
        {canPickStandardSet && ssOptions.length > 0 && (
          <SegControl
            inline
            label="Standards"
            value={curSs}
            onChange={(v) => setSsOverride(v)}
            options={ssOptions}
          />
        )}
      </ControlBar>

      {/* SHAPE + DISPLAY — everything below re-renders from the response already
          in hand, so it sits with the graphic rather than in the scope bar. The
          title changes with the view because that is what View does. */}
      <SectionHead
        title={matrix ? "Units × schools" : "Mastery by unit"}
        controls={
          <>
            {canCompareSchools && (
              <SegControl
                inline
                label="View"
                value={view}
                onChange={handleSetView}
                options={[
                  ["list", "List"],
                  ["matrix", "Schools matrix"],
                ]}
              />
            )}
            {canCompareSchools && !matrix && (
              <SegControl
                inline
                label="Expand into"
                value={split}
                onChange={handleSetSplit}
                options={[
                  ["standards", "Standards"],
                  ["schools", "Schools"],
                ]}
              />
            )}
            {matrix && (
              <SegControl
                inline
                label="Cell"
                value={cellMetric}
                onChange={handleSetCellMetric}
                options={[
                  ["reached", "% reached"],
                  ["delta", "vs district"],
                ]}
              />
            )}
            {/* Sort/Bar/Content level/Min learners: set once, then left alone.
                Two of them can HIDE rows, so the count on the trigger and the
                legend's trailing line both stay explicit about it. */}
            <DisplayMenu changed={displayChanged}>
              <SegControl
                label="Sort"
                value={sort}
                onChange={setSort}
                options={[
                  ["focus", "Focus"],
                  ["lift", "ABii lift"],
                  ["activity", "Activity"],
                  ...(matrix ? [["spread", "Spread"]] : []),
                ]}
              />
              {!matrix && (
                <SegControl
                  label="Bar"
                  value={mode}
                  onChange={setMode}
                  options={[
                    ["share", "Share"],
                    ["volume", "Volume"],
                  ]}
                />
              )}
              <SegControl
                label="Min learners"
                value={minLearners}
                onChange={setMinLearners}
                options={COVERAGE_OPTIONS.map((n) => [n, String(n)])}
              />
            </DisplayMenu>
          </>
        }
      />

      {body}
      {!body && (
      <>
      {/* Summary strip — the same StatRail the Overview's summary band uses, so
          the two pages don't present one reading of one thing in two different
          house styles. The per-tile borders went with it: these four numbers are
          one summary, not four separate objects, and "ABii-assisted" carries the
          finding through its value color rather than through an accent border. */}
      <Card style={{ marginBottom: 18 }}>
        <StatRail>
          <RailStat
            label="Cohort"
            value={cohortLabel(grade)}
            sub={`${data.roster_size} learners · ${curSs}`}
          />
          <RailStat label="Mastery events (shown)" value={totEvents.toLocaleString()}>
            <div style={{ display: "flex", height: 8, borderRadius: 4, overflow: "hidden", gap: 2, marginTop: 8 }}>
              <span style={{ flex: totals.out || 0.001, background: C_OUT }} />
              <span style={{ flex: totals.evt || 0.001, background: C_EVT }} />
              <span style={{ flex: totals.not || 0.001, background: C_NOT }} />
            </div>
          </RailStat>
          <RailStat
            label="Reached mastery"
            value={`${pct(totals.out + totals.evt, totEvents)}%`}
            sub="of mastery events"
          />
          <RailStat
            label="ABii-assisted"
            color={C_EVT}
            value={`${pct(totals.evt, totEvents)}%`}
            sub={`${totals.evt.toLocaleString()} masteries needed a re-teach`}
          />
          {/* Only in the unfiltered view: under a directional filter this is
              100% by construction and the rail drops back to four tiles. */}
          {contentLevel === "all" && levelShown > 0 && (
            <RailStat
              label="On grade level"
              value={`${pct(levelTotals.on, levelShown)}%`}
              // Same order as the bar, and zero poles are dropped from both:
              // "0% above" is noise, and it would name a band the bar has no
              // segment for.
              sub={
                ["below", "above", "unknown"]
                  .filter((k) => levelTotals[k] > 0)
                  .map((k) => `${pct(levelTotals[k], levelShown)}% ${k}`)
                  .join(" · ") || "all of it on grade"
              }
            >
              <LevelCompositionBar
                byLevel={levelTotals}
                total={levelShown}
                onSelect={handleSetContentLevel}
              />
            </RailStat>
          )}
        </StatRail>
      </Card>

      {matrix ? (
        <UnitSchoolMatrix
          units={ranked}
          groups={cached.groups}
          byGroup={cached.byGroup}
          metric={cellMetric}
          loading={detail.loading && !cached.groups.length}
          error={detail.error && !cached.groups.length}
          gradeText={cohortLabel(grade).toLowerCase()}
        />
      ) : (
      <>
      {/* legend */}
      <Legend
        items={MASTERY_BANDS.map(({ color, label, note }) => ({
          color,
          label,
          note: note || undefined,
        }))}
        trailing={
          <>
            {ranked.length} units ≥ {minLearners} learners ·{" "}
            {cohortLabel(grade).toLowerCase()}
            {subject && ` · ${subject.toLowerCase()}`}
            {contentLevel !== "all" &&
              ` · ${LEVEL_REL[contentLevel].long} only`}
            {contentLevel === "all" &&
              levelTotals.unknown > 0 &&
              ` · ${pct(levelTotals.unknown, levelShown)}% of events at an unknown content level`}
            {compare && ` · expands into ${orgunits.length} schools`}
          </>
        }
      />

      {/* rows */}
      <TableCard>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: GRID_COLS,
            gap: COL_GAP,
            padding: "8px",
            fontSize: 11,
            letterSpacing: ".05em",
            textTransform: "uppercase",
            color: MUTED,
            fontWeight: 600,
            background: TRAY,
          }}
        >
          <div />
          <div>Unit</div>
          <div>
            Mastery composition{" "}
            <span style={{ textTransform: "none", letterSpacing: 0, fontWeight: 400 }}>
              ({mode === "share" ? "normalized to 100%" : "bar length = events"}
              {compare ? " · expand to compare schools" : ""})
            </span>
          </div>
          <div style={{ textAlign: "right" }}>Events</div>
        </div>
        {ranked.length === 0 && (
          <TableNote>
            No units clear the {minLearners}-learner coverage bar for this cohort.
            Lower “Min learners” under <b>Display</b>
            {contentLevel !== "all"
              ? `, set “Content level” back to all levels (this cohort may have no ${LEVEL_REL[contentLevel].long} work at all),`
              : ""}{" "}
            or pick a grade with more activity.
          </TableNote>
        )}
        {ranked.map((u) => (
          <UnitRow
            key={u.unit_id}
            rec={u}
            mode={mode}
            scale={scale}
            standards={(data.standards || {})[String(u.unit_id)]}
            expanded={expanded === u.unit_id}
            onToggle={() => handleToggleUnit(u.unit_id)}
            split={split}
            detail={expanded === u.unit_id ? detail : undefined}
            sub={sub}
            onSub={subEnabled ? handleSub : undefined}
            selectedSchool={school}
          />
        ))}
      </TableCard>

      {/* thin-data section */}
      {thin.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <GhostButton onClick={() => setShowThin((v) => !v)}>
            {showThin ? "Hide" : "Show"} {thin.length} units with thin data (&lt;{" "}
            {minLearners} learners)
          </GhostButton>
          {showThin && (
            <TableCard style={{ marginTop: 8 }}>
              {thin
                .slice()
                .sort(SORTERS[sort])
                .map((u) => (
                  <UnitRow
                    key={u.unit_id}
                    rec={u}
                    mode={mode}
                    scale={scale}
                    standards={(data.standards || {})[String(u.unit_id)]}
                    expanded={expanded === u.unit_id}
                    onToggle={() => handleToggleUnit(u.unit_id)}
                    split={split}
                    detail={expanded === u.unit_id ? detail : undefined}
                    sub={sub}
                    onSub={subEnabled ? handleSub : undefined}
                    selectedSchool={school}
                    thin
                  />
                ))}
            </TableCard>
          )}
        </div>
      )}

      <PlotNote
        label="How to read these bars"
        details={
        <>
        The cohort is pooled across the whole organization, so it has coverage
        even where no single classroom does, and prior years are excluded. Units
        below the coverage bar are held back as “thin data,” since a
        low-coverage bar is a weak signal. Sort by <b>Focus</b> to rank the gap
        (amber); the blue <b>Eventually</b> band is ABii’s re-teach lift.
        {grade === GRADE_ALL && (
          <>
            {" "}
            <b>All grades</b> pools every grade in the roster: use it where a
            single cohort is too thin to read.
          </>
        )}{" "}
        <b>Content level</b> compares each unit’s grade against the grade of the
        learner who worked it — per learner, not per cohort, so the same grade-1
        unit is on grade for a grade-1 learner and remediation for a grade-3 one,
        and the reading holds on <b>All grades</b> too. All levels is the
        default because off-level work is real remediation/acceleration signal,
        not an error; each row’s chip says where its
        work sat — a direction when the unit was worked from one grade, otherwise
        the share of it that was on grade (hover for the full split). Filtering to one direction re-counts the bars from only those
        events, so a unit can appear under both <i>Below</i> and <i>On grade</i>
        with a different composition in each — the bands of the{" "}
        <b>On grade level</b> figure above select those directions directly.{" "}
        Hover a standard for its description. Never-attempted standards can’t
        appear — this shows mastery on what’s been taught.
        {canCompareSchools && (
          <>
            {" "}
            <b>Expanding into schools</b> decomposes the district bar above it:
            each school’s bar covers only its own learners in this cohort, on the
            standards <i>they</i> attempted, so two schools that worked different
            parts of a unit aren’t strictly comparable — the trailing
            <i> events · learners</i> is there to say how much each bar rests on,
            and schools under {SCHOOL_MIN_LEARNERS} learners are marked{" "}
            <i>thin</i> rather than hidden. A learner enrolled in two schools
            counts in both, so school rows can sum past the district row.
            {selectedSchoolName && compare
              ? ` ${selectedSchoolName} is marked (▸) in every comparison.`
              : ""}
          </>
        )}
        </>
        }
      >
        Each bar is one unit’s mastery reached <b>within the selected school
        year</b> for {cohortLabel(grade).toLowerCase()}.
      </PlotNote>
      </>
      )}
      </>
      )}
    </div>
  );
};

export { UnitMasteryTab };
